import { useState, useEffect, useCallback } from 'react'
import {
  Award, Gift, Settings, TrendingUp, Users,
  Plus, Edit2, Trash2, Save, X, AlertCircle,
  Loader2, RefreshCw, BarChart3, DollarSign,
  CheckCircle, Crown, Star, Zap
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { loyaltyService, LoyaltyTier, Reward, LoyaltyConfig, LoyaltyTransaction } from '../services/loyaltyService'

type TabType = 'config' | 'tiers' | 'rewards' | 'reports'

const LoyaltyPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>('config')
  const [loading, setLoading] = useState(true)

  // Config state
  const [config, setConfig] = useState<LoyaltyConfig | null>(null)
  const [editConfig, setEditConfig] = useState<Partial<LoyaltyConfig> | null>(null)

  // Tiers state
  const [tiers, setTiers] = useState<LoyaltyTier[]>([])
  const [editingTier, setEditingTier] = useState<LoyaltyTier | null>(null)
  const [showTierForm, setShowTierForm] = useState(false)
  const [tierForm, setTierForm] = useState<Omit<LoyaltyTier, 'tier_id'>>({
    name: '', min_points: 0, max_points: null,
    benefits: [], multiplier: 1, color: '#6366f1',
  })

  // Rewards state
  const [rewards, setRewards] = useState<Reward[]>([])
  const [showRewardForm, setShowRewardForm] = useState(false)
  const [editingReward, setEditingReward] = useState<Reward | null>(null)
  const [rewardForm, setRewardForm] = useState<Omit<Reward, 'reward_id'>>({
    name: '', description: '', points_cost: 0,
    image_url: null, is_available: true,
    quantity_remaining: null, category: null,
  })

  // Reports state
  const [pointsSummary, setPointsSummary] = useState<any>(null)
  const [tierDistribution, setTierDistribution] = useState<any[]>([])

  // Manual adjustment
  const [adjustUserId, setAdjustUserId] = useState('')
  const [adjustPoints, setAdjustPoints] = useState(0)
  const [adjustReason, setAdjustReason] = useState('')
  const [adjusting, setAdjusting] = useState(false)

  const loadConfig = useCallback(async () => {
    try {
      const res = await loyaltyService.getConfig()
      if (res.success) setConfig(res.data)
    } catch { /* ignore */ }
  }, [])

  const loadTiers = useCallback(async () => {
    try {
      const res = await loyaltyService.getTiers()
      if (res.success) setTiers(res.data)
    } catch { /* ignore */ }
  }, [])

  const loadRewards = useCallback(async () => {
    try {
      const res = await loyaltyService.getRewards()
      if (res.success) setRewards(res.data)
    } catch { /* ignore */ }
  }, [])

  const loadReports = useCallback(async () => {
    try {
      const [summaryRes, distRes] = await Promise.all([
        loyaltyService.getPointsSummary(),
        loyaltyService.getTierDistribution(),
      ])
      if (summaryRes.success) setPointsSummary(summaryRes.data)
      if (distRes.success) setTierDistribution(distRes.data)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    setLoading(true)
    const loaders: Record<TabType, () => Promise<void>> = {
      config: loadConfig,
      tiers: loadTiers,
      rewards: loadRewards,
      reports: loadReports,
    }
    loaders[activeTab]().finally(() => setLoading(false))
  }, [activeTab, loadConfig, loadTiers, loadRewards, loadReports])

  // Config handlers
  const handleSaveConfig = async () => {
    if (!editConfig) return
    try {
      const res = await loyaltyService.updateConfig(editConfig)
      if (res.success) {
        setConfig(res.data)
        setEditConfig(null)
        toast.success('Configuration mise à jour')
      }
    } catch {
      toast.error('Erreur lors de la mise à jour')
    }
  }

  // Tier handlers
  const handleSaveTier = async () => {
    try {
      if (editingTier) {
        const res = await loyaltyService.updateTier(editingTier.tier_id, tierForm)
        if (res.success) {
          toast.success('Palier mis à jour')
          setShowTierForm(false)
          setEditingTier(null)
          loadTiers()
        }
      } else {
        const res = await loyaltyService.createTier(tierForm)
        if (res.success) {
          toast.success('Palier créé')
          setShowTierForm(false)
          loadTiers()
        }
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde du palier')
    }
  }

  const handleDeleteTier = async (tierId: string) => {
    if (!confirm('Supprimer ce palier ?')) return
    try {
      const res = await loyaltyService.deleteTier(tierId)
      if (res.success) {
        toast.success('Palier supprimé')
        loadTiers()
      }
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  const openTierForm = (tier?: LoyaltyTier) => {
    if (tier) {
      setEditingTier(tier)
      setTierForm({
        name: tier.name, min_points: tier.min_points,
        max_points: tier.max_points, benefits: tier.benefits,
        multiplier: tier.multiplier, color: tier.color,
      })
    } else {
      setEditingTier(null)
      setTierForm({ name: '', min_points: 0, max_points: null, benefits: [], multiplier: 1, color: '#6366f1' })
    }
    setShowTierForm(true)
  }

  // Reward handlers
  const handleSaveReward = async () => {
    try {
      if (editingReward) {
        const res = await loyaltyService.updateReward(editingReward.reward_id, rewardForm)
        if (res.success) {
          toast.success('Récompense mise à jour')
          setShowRewardForm(false)
          setEditingReward(null)
          loadRewards()
        }
      } else {
        const res = await loyaltyService.createReward(rewardForm)
        if (res.success) {
          toast.success('Récompense créée')
          setShowRewardForm(false)
          loadRewards()
        }
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde de la récompense')
    }
  }

  const handleDeleteReward = async (rewardId: string) => {
    if (!confirm('Supprimer cette récompense ?')) return
    try {
      const res = await loyaltyService.deleteReward(rewardId)
      if (res.success) {
        toast.success('Récompense supprimée')
        loadRewards()
      }
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  const openRewardForm = (reward?: Reward) => {
    if (reward) {
      setEditingReward(reward)
      setRewardForm({
        name: reward.name, description: reward.description,
        points_cost: reward.points_cost, image_url: reward.image_url,
        is_available: reward.is_available,
        quantity_remaining: reward.quantity_remaining,
        category: reward.category,
      })
    } else {
      setEditingReward(null)
      setRewardForm({
        name: '', description: '', points_cost: 0,
        image_url: null, is_available: true,
        quantity_remaining: null, category: null,
      })
    }
    setShowRewardForm(true)
  }

  // Manual adjustment
  const handleAdjustPoints = async () => {
    if (!adjustUserId || adjustPoints === 0) {
      toast.error('Veuillez remplir tous les champs')
      return
    }
    setAdjusting(true)
    try {
      const res = await loyaltyService.adjustPoints(adjustUserId, adjustPoints, adjustReason)
      if (res.success) {
        toast.success(`Points ${adjustPoints > 0 ? 'ajoutés' : 'retirés'} avec succès`)
        setAdjustUserId('')
        setAdjustPoints(0)
        setAdjustReason('')
      }
    } catch {
      toast.error('Erreur lors de l\'ajustement des points')
    } finally {
      setAdjusting(false)
    }
  }

  const tabs: { id: TabType; label: string; icon: any }[] = [
    { id: 'config', label: 'Configuration', icon: Settings },
    { id: 'tiers', label: 'Paliers', icon: Crown },
    { id: 'rewards', label: 'Récompenses', icon: Gift },
    { id: 'reports', label: 'Rapports', icon: BarChart3 },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Gestion de la fidélité</h1>
        <p className="mt-2 text-gray-600">Configurez et gérez le programme de fidélité</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
        </div>
      ) : (
        <>
          {/* Config Tab */}
          {activeTab === 'config' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Règles de fidélité</h2>
              
              {editConfig ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Points par devise</label>
                    <input
                      type="number"
                      value={editConfig.points_per_currency || 0}
                      onChange={(e) => setEditConfig({ ...editConfig, points_per_currency: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={handleSaveConfig}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </button>
                    <button
                      onClick={() => setEditConfig(null)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Points par unité de devise</p>
                      <p className="text-2xl font-bold text-gray-900">{config?.points_per_currency || 0}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditConfig(config ? { ...config } : { points_per_currency: 1, bonus_points: {}, tier_upgrade_thresholds: {} })}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Modifier la configuration
                  </button>
                </div>
              )}

              {/* Manual Points Adjustment */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ajustement manuel des points</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID Utilisateur</label>
                    <input
                      type="text"
                      value={adjustUserId}
                      onChange={(e) => setAdjustUserId(e.target.value)}
                      placeholder="user_id"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Points (+/-)</label>
                    <input
                      type="number"
                      value={adjustPoints}
                      onChange={(e) => setAdjustPoints(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Raison</label>
                    <input
                      type="text"
                      value={adjustReason}
                      onChange={(e) => setAdjustReason(e.target.value)}
                      placeholder="Motif de l'ajustement"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleAdjustPoints}
                  disabled={adjusting}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {adjusting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
                  Ajuster les points
                </button>
              </div>
            </div>
          )}

          {/* Tiers Tab */}
          {activeTab === 'tiers' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Paliers de fidélité</h2>
                <button
                  onClick={() => openTierForm()}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau palier
                </button>
              </div>

              {showTierForm && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    {editingTier ? 'Modifier le palier' : 'Nouveau palier'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                      <input
                        type="text"
                        value={tierForm.name}
                        onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Points minimum</label>
                      <input
                        type="number"
                        value={tierForm.min_points}
                        onChange={(e) => setTierForm({ ...tierForm, min_points: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Points maximum</label>
                      <input
                        type="number"
                        value={tierForm.max_points || ''}
                        onChange={(e) => setTierForm({ ...tierForm, max_points: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Multiplicateur</label>
                      <input
                        type="number"
                        step="0.1"
                        value={tierForm.multiplier}
                        onChange={(e) => setTierForm({ ...tierForm, multiplier: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                      <input
                        type="color"
                        value={tierForm.color}
                        onChange={(e) => setTierForm({ ...tierForm, color: e.target.value })}
                        className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Avantages (un par ligne)</label>
                      <textarea
                        value={tierForm.benefits.join('\n')}
                        onChange={(e) => setTierForm({ ...tierForm, benefits: e.target.value.split('\n').filter(b => b.trim()) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={3}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={handleSaveTier} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                      <Save className="w-4 h-4 inline mr-2" />
                      {editingTier ? 'Mettre à jour' : 'Créer'}
                    </button>
                    <button onClick={() => setShowTierForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tiers.map((tier) => (
                  <div key={tier.tier_id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: tier.color + '20' }}>
                          <Crown className="w-4 h-4" style={{ color: tier.color }} />
                        </div>
                        <h3 className="font-bold text-gray-900">{tier.name}</h3>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openTierForm(tier)} className="p-1.5 text-gray-400 hover:text-primary-600">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteTier(tier.tier_id)} className="p-1.5 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {tier.min_points.toLocaleString()} - {tier.max_points?.toLocaleString() || '∞'} pts
                    </p>
                    <p className="text-xs text-gray-500 mb-2">Multiplicateur: x{tier.multiplier}</p>
                    <ul className="space-y-1">
                      {tier.benefits?.map((b, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rewards Tab */}
          {activeTab === 'rewards' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Catalogue de récompenses</h2>
                <button
                  onClick={() => openRewardForm()}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle récompense
                </button>
              </div>

              {showRewardForm && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    {editingReward ? 'Modifier la récompense' : 'Nouvelle récompense'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                      <input
                        type="text"
                        value={rewardForm.name}
                        onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Coût en points</label>
                      <input
                        type="number"
                        value={rewardForm.points_cost}
                        onChange={(e) => setRewardForm({ ...rewardForm, points_cost: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={rewardForm.description}
                        onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                      <input
                        type="text"
                        value={rewardForm.category || ''}
                        onChange={(e) => setRewardForm({ ...rewardForm, category: e.target.value || null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quantité restante</label>
                      <input
                        type="number"
                        value={rewardForm.quantity_remaining || ''}
                        onChange={(e) => setRewardForm({ ...rewardForm, quantity_remaining: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rewardForm.is_available}
                          onChange={(e) => setRewardForm({ ...rewardForm, is_available: e.target.checked })}
                          className="rounded border-gray-300 text-primary-600"
                        />
                        <span className="text-sm text-gray-700">Disponible</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={handleSaveReward} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                      <Save className="w-4 h-4 inline mr-2" />
                      {editingReward ? 'Mettre à jour' : 'Créer'}
                    </button>
                    <button onClick={() => setShowRewardForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rewards.map((reward) => (
                  <div key={reward.reward_id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {reward.image_url && (
                      <div className="h-32 bg-gray-100">
                        <img src={reward.image_url} alt={reward.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{reward.name}</h3>
                          {reward.category && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{reward.category}</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openRewardForm(reward)} className="p-1 text-gray-400 hover:text-primary-600">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDeleteReward(reward.reward_id)} className="p-1 text-gray-400 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{reward.description}</p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <span className="text-primary-600 font-bold">{reward.points_cost.toLocaleString()} pts</span>
                        <span className={`text-xs ${reward.is_available ? 'text-green-600' : 'text-red-600'}`}>
                          {reward.is_available ? 'Disponible' : 'Indisponible'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Rapports de fidélité</h2>

              {/* Summary Cards */}
              {pointsSummary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Points gagnés</p>
                        <p className="text-2xl font-bold text-gray-900">{pointsSummary.total_points_earned?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Award className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Points échangés</p>
                        <p className="text-2xl font-bold text-gray-900">{pointsSummary.total_points_redeemed?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Points expirés</p>
                        <p className="text-2xl font-bold text-gray-900">{pointsSummary.total_points_burned?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Utilisateurs actifs</p>
                        <p className="text-2xl font-bold text-gray-900">{pointsSummary.active_users?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tier Distribution */}
              {tierDistribution.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Répartition par palier</h3>
                  <div className="space-y-4">
                    {tierDistribution.map((item) => (
                      <div key={item.tier}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{item.tier}</span>
                          <span className="text-gray-500">{item.count} ({item.percentage?.toFixed(1)}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-primary-500 h-2.5 rounded-full transition-all"
                            style={{ width: `${item.percentage || 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Refresh */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={loadReports}
                  className="inline-flex items-center px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default LoyaltyPage
